import { type CustomFieldProps, useField } from 'informed';
import type React from 'react';
import {
  type FC,
  type InputHTMLAttributes,
  useCallback,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { ValidatorMessage } from '@/helpers/validators';
import type { CustomFormFieldProps } from './contracts';
import {
  CheckboxBlock,
  type CheckboxBlockProps,
} from './primitives/CheckboxBlock';
import { getErrorTranslation } from './utils';

export const Checkbox: FC<
  Omit<CheckboxBlockProps, 'error' | 'id'> &
    CustomFormFieldProps &
    InputHTMLAttributes<HTMLInputElement>
> = ({
  translateError = true,
  formId,
  onClick,
  label,
  tooltip,
  error: propsError,
  styleScheme,
  testId,
  checked = false,
  isHidden = false,
  ...fieldProps
}) => {
  const { t } = useTranslation();

  const { fieldState, fieldApi, render, ref } = useField<
    CustomFieldProps<CustomFormFieldProps, boolean>,
    boolean
  >(fieldProps);

  const { value, error } = fieldState;
  const { setValue, setTouched } = fieldApi;

  const id = `${fieldProps?.name}${'-' + formId || ''}`;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.checked);
    setTouched(true);
  };

  const getError = useCallback(() => {
    if (propsError) propsError;

    if (error != null && translateError) {
      return getErrorTranslation(t, error as ValidatorMessage);
    }

    return error;
  }, [t, propsError, translateError, error]);

  useEffect(() => {
    setValue(checked);
  }, [checked]);

  return render(
    <CheckboxBlock
      checked={value}
      error={<>{getError()}</>}
      id={id}
      isHidden={isHidden}
      label={label}
      onChange={handleChange}
      onClick={onClick}
      ref={ref}
      styleScheme={styleScheme}
      testId={testId}
      tooltip={tooltip}
    />
  );
};
